import { findEnclosingZoneId } from "./node-tree";
import type { Cable, System, ZoneNode } from "./types";

/**
 * Canonical key for a zone pair. Sorted, so `a|b` and `b|a` are one entry.
 * Lives here, not in the store: `system-draft.ts` imports it and must stay
 * edge-safe.
 */
export function zonePairKey(a: string, b: string): string {
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}

/**
 * Slack from a zone distance to a cable length. `cable-length-modal.tsx` keeps
 * its own copy of this number; the two must match.
 */
export const ZONE_SLACK_M = 0.5;

const MIN_LENGTH_M = 0.1;

/**
 * Ceiling on the slack as a fraction of the run: a flat 0.5 m taken off a
 * 0.6 m link would return the floor rather than an answer.
 */
const SLACK_FRACTION = 0.25;

/** Lengths are metres to one decimal everywhere in the app. */
function round1(v: number): number {
  return Math.round(v * 10) / 10;
}

export function zonesOf(system: System): ZoneNode[] {
  return system.nodes.filter((n): n is ZoneNode => n.kind === "zone");
}

/**
 * Which zone each node sits in, resolved once: `findEnclosingZoneId` re-scans
 * the node list per node, so asking it per cable end is O(n²).
 */
export function zoneIdByNode(system: System): Map<string, string> {
  const byNode = new Map<string, string>();
  for (const node of system.nodes) {
    if (node.kind === "zone") continue;
    const zoneId = findEnclosingZoneId(node.id, system.nodes);
    if (zoneId) byNode.set(node.id, zoneId);
  }
  return byNode;
}

export function cablesCrossingZones(
  system: System,
  zoneA: string,
  zoneB: string,
  zoneByNode: Map<string, string> = zoneIdByNode(system),
): Cable[] {
  if (zoneA === zoneB) return [];
  return system.cables.filter((cable) => {
    const from = zoneByNode.get(cable.fromNodeId);
    const to = zoneByNode.get(cable.toNodeId);
    if (!from || !to) return false;
    return (
      (from === zoneA && to === zoneB) || (from === zoneB && to === zoneA)
    );
  });
}

/**
 * The distance a pair's existing cables imply, or `null` when nothing crosses.
 * Median, not mean, so one 8 m stern-light run can't drag it. The slack comes
 * back off: this number is the gap, not the cable.
 */
export function impliedZoneDistanceM(
  system: System,
  zoneA: string,
  zoneB: string,
  zoneByNode?: Map<string, string>,
): number | null {
  const crossing = cablesCrossingZones(system, zoneA, zoneB, zoneByNode);
  if (!crossing.length) return null;
  const lengths = crossing.map((c) => c.lengthM).sort((a, b) => a - b);
  const mid = Math.floor(lengths.length / 2);
  const median =
    lengths.length % 2 === 1 ? lengths[mid] : (lengths[mid - 1] + lengths[mid]) / 2;
  const slack = Math.min(ZONE_SLACK_M, median * SLACK_FRACTION);
  return round1(Math.max(MIN_LENGTH_M, median - slack));
}

/**
 * The distance in force for a pair: stored, else what the cables imply. The
 * fallback is what lets the first edit be a shift rather than flattening every
 * run onto one number.
 */
export function effectiveZoneDistanceM(
  system: System,
  zoneA: string,
  zoneB: string,
  zoneByNode?: Map<string, string>,
): number | null {
  const stored = system.zoneDistances?.[zonePairKey(zoneA, zoneB)];
  if (typeof stored === "number" && Number.isFinite(stored)) return stored;
  return impliedZoneDistanceM(system, zoneA, zoneB, zoneByNode);
}

export type CableRelength = { id: string; lengthM: number };

/**
 * What changing a zone pair's distance does to the cables that cross it: every
 * one shifts by the difference. Empty list when nothing moves.
 */
export function planZoneDistanceRelength(
  system: System,
  zoneA: string,
  zoneB: string,
  nextDistanceM: number,
): CableRelength[] {
  if (!Number.isFinite(nextDistanceM) || nextDistanceM < 0) return [];
  const zoneByNode = zoneIdByNode(system);
  const crossing = cablesCrossingZones(system, zoneA, zoneB, zoneByNode);
  if (!crossing.length) return [];
  const previous = effectiveZoneDistanceM(system, zoneA, zoneB, zoneByNode);
  if (previous === null) return [];
  const delta = round1(nextDistanceM - previous);
  if (delta === 0) return [];
  return crossing
    .map((cable) => ({
      id: cable.id,
      lengthM: round1(Math.max(MIN_LENGTH_M, cable.lengthM + delta)),
    }))
    .filter((next, i) => next.lengthM !== crossing[i].lengthM);
}

/**
 * Every zone pair with cables across it, and the distance those cables imply —
 * what a freshly generated system is handed as its `zoneDistances`.
 */
export function impliedZoneDistances(system: System): Record<string, number> {
  const zones = zonesOf(system);
  const zoneByNode = zoneIdByNode(system);
  const out: Record<string, number> = {};
  for (let i = 0; i < zones.length; i++) {
    for (let j = i + 1; j < zones.length; j++) {
      const distance = impliedZoneDistanceM(
        system,
        zones[i].id,
        zones[j].id,
        zoneByNode,
      );
      if (distance !== null) out[zonePairKey(zones[i].id, zones[j].id)] = distance;
    }
  }
  return out;
}
